import React from 'react';

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { BRAND_COLORS } from '~/components/brand/brandTokens';

import { Model } from './presentationModel';

type Target = Model['targetRows'][number];
type SubscriptionView = 'PCM' | 'MM';

export const SUBSCRIPTION_TABS: ReadonlyArray<{ id: string; label: string; view: SubscriptionView }> = [
  { id: 'subscriptions', label: 'Subscriptions', view: 'PCM' },
  { id: 'subscriptions-mm', label: 'Subscriptions - MM', view: 'MM' },
];
type ValueColumn = [string, (row: Target) => number | null, Intl.NumberFormat];

const SHEETS: Record<
  SubscriptionView,
  {
    rsmRegions: Array<string>;
    metrics: Array<string>;
    regionOrder: Array<string>;
    override: boolean;
    targetDigits: number;
  }
> = {
  PCM: {
    rsmRegions: [
      'MEA (Middle East & Asia)',
      'AusPac (Australia & South Pacific)',
      'Europe',
      'New Zealand & Sri Lanka',
      'North America',
      'Tours',
      'AU, South Pacific & Hawaii',
      'US',
      'EU',
      'Market Manager Lead (PCM)',
      'Indonesia',
      'Thailand',
      'Middle East, Maldives, Japan',
      'Lifestyle Brands & White Label',
      'Cruises',
      'Asia & ME & Maldives & SthPac',
      'AU/NZ',
      'EU/US',
      'Hotel',
      'India',
    ],
    metrics: [
      'net_margin_adjusted',
      'net_signings',
      'proposals',
      'calls',
      'meetings',
      'reopened_signings',
      'activity_point',
    ],
    regionOrder: [
      'Nusa Dua, Sanur, Jimbaran',
      'Seminyak, Legian, Kuta, Canggu & Uluwatu',
      'Khao Lak',
      'Ubud, North Bali, Indonesia Islands, Singapore, Hong Kong & Malaysia',
      'Maldives & UAE',
      'Phuket 1 & Vietnam 1',
      'CBR, Regional NSW, Regional WA',
      'Domestic, International',
      'East Coast, Hawaii, Caribbean',
      'Fiji',
      'Greece',
      'France, Morocco, Mauritius & Seychelles',
      'Gold Coast, Whitsundays, Cook Islands, Vanuatu and New Caledonia',
      'Italy',
      'Koh Samui, Phuket 2, Pattaya & Northern Thailand',
      'Bangkok, Hua Hin, Japan & Vietnam 2',
      'QLD (Excl Gold Coast & Whitsundays)',
      'Regional VIC, TAS, SA, NT',
      'Spain, Portugal, Hungary, Netherlands & Czech Republic',
      'Sydney, Melbourne & Perth',
      'UK (Regional NSW, Regional WA)',
      'West Coast, South USA, Canada & Mexico',
    ],
    override: true,
    targetDigits: 0,
  },
  MM: {
    rsmRegions: ['Market Manager Lead'],
    metrics: ['net_margin_adjusted', 'net_signings', 'proposals', 'calls', 'meetings', 'mpoi_actual', 'activity_point'],
    regionOrder: [
      'Marketplace (East Asia, NZ)',
      'Lifestyle (East Asia, NZ)',
      'Marketplace (Indonesia)',
      'Lifestyle (Indonesia)',
      'Marketplace (NSW, TAS, ACT, SA, NT)',
      'Lifestyle (NSW, TAS, ACT, SA, NT)',
      'Marketplace (QLD, VIC, WA)',
      'Lifestyle (QLD, VIC, WA)',
      'Marketplace (SE Asia, ME)',
      'Lifestyle (SE Asia, ME)',
      'Marketplace (SG, America 2, Caribbean)',
      'Lifestyle (SG, America 2, Caribbean)',
      'Marketplace (South Pacific, America 1, Maldives)',
      'Lifestyle (South Pacific, America 1, Maldives)',
      'Lifestyle on LE (East Asia, NZ)',
      'Lifestyle on LE (Indonesia)',
      'Lifestyle on LE (NSW, TAS, ACT, SA, NT)',
      'Lifestyle on LE (QLD, VIC, WA)',
      'Lifestyle on LE (SE Asia, ME)',
      'Lifestyle on LE (SG, America 2, Caribbean)',
      'Lifestyle on LE(South Pacific, America 1, Maldives)',
      'New Zealand',
    ],
    override: false,
    targetDigits: 2,
  },
};
const MARGIN_ONLY_REGIONS = [
  'Marketplace (Mauritius, Seychelles and South Korea)',
  'Marketplace (UAE, Qatar and Oman)',
  'Bedbanks Marriott',
  'TNQ',
  'Reg QLD Coast & Inland, Reg VIC & GC Tier 2/3',
  'Sunshine Coast & Whitsundays',
];
const ACTIVITY_METRICS = ['net_signings', 'proposals', 'calls', 'meetings', 'activity_point', 'reopened_signings'];
const HOMES_AND_VILLAS = 'Marketplace (Homes & Villa)';

const relevant = (row: Target) =>
  !(MARGIN_ONLY_REGIONS.includes(row.region ?? '') && ACTIVITY_METRICS.includes(row.metric)) &&
  !(row.region === HOMES_AND_VILLAS && row.metric === 'mpoi_actual');

const rank = (order: Array<string>, value: string | null) => {
  const index = order.indexOf(value ?? '');
  return index < 0 ? order.length : index;
};

export function subscriptionRows(rows: Model['targetRows'], view: SubscriptionView): Model['targetRows'] {
  const sheet = SHEETS[view];
  return rows
    .filter(
      (row) => sheet.rsmRegions.includes(row.rsmRegion ?? '') && sheet.metrics.includes(row.metric) && relevant(row),
    )
    .sort(
      (a, b) =>
        rank(sheet.rsmRegions, a.rsmRegion) - rank(sheet.rsmRegions, b.rsmRegion) ||
        (a.person ?? '').localeCompare(b.person ?? '') ||
        rank(sheet.regionOrder, a.region) - rank(sheet.regionOrder, b.region) ||
        (a.region ?? '').localeCompare(b.region ?? '') ||
        sheet.metrics.indexOf(a.metric) - sheet.metrics.indexOf(b.metric),
    );
}

const decimals = (digits: number) =>
  new Intl.NumberFormat('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const percentOne = new Intl.NumberFormat('en-AU', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fixed = (value: number | null, formatter: Intl.NumberFormat) =>
  value !== null && Number.isFinite(value) ? formatter.format(value) : 'Unknown';
const statusColour = (row: Target) =>
  row.onTrack === 'Yes' ? 'success.main' : row.onTrack === 'No' ? 'error.main' : 'text.secondary';
const groupKey = (row: Target, depth: number) =>
  JSON.stringify([row.rsmRegion, row.person, row.region].slice(0, depth));

export default function CommercialKpiSubscriptions({
  rows,
  quarter,
  view,
}: {
  rows: Model['targetRows'];
  quarter: string;
  view: SubscriptionView;
}) {
  const sheet = SHEETS[view];
  const shown = subscriptionRows(rows, view);
  const values: Array<ValueColumn> = [
    ['Actual', (row) => row.actual, decimals(2)],
    ['Forecast', (row) => row.forecast, decimals(2)],
    ['Target', (row) => row.target, decimals(sheet.targetDigits)],
    ...(sheet.override ? [['Override', (row: Target) => row.override ?? 0, decimals(0)] as ValueColumn] : []),
    ['EOP P%', (row) => row.forecastRatio, percentOne],
  ];
  const headings = ['RSM Region', view === 'MM' ? 'MM Name' : 'PCM Name', 'PCM Region', 'Metric Name', 'On Track'];
  const firstChange = (index: number) =>
    index === 0
      ? 1
      : ([1, 2, 3].find((depth) => groupKey(shown[index - 1], depth) !== groupKey(shown[index], depth)) ?? 0);
  const positions: Array<number> = [];
  shown.forEach((row, index) =>
    positions.push(index && groupKey(shown[index - 1], 3) === groupKey(row, 3) ? positions[index - 1] + 1 : 0),
  );
  return (
    <Box>
      <Typography component="h2" sx={{ fontSize: 18, fontWeight: 500, mb: 1 }}>
        Quarter {quarter.split('-').reverse().join('/')}
      </Typography>
      <Table
        size="small"
        aria-label={view === 'MM' ? 'MM subscriptions' : 'PCM subscriptions'}
        sx={{
          width: 'auto',
          '& .MuiTableCell-root': { py: 0.25, px: 1, fontSize: 12, lineHeight: 1.4, borderBottom: 0 },
          '& .MuiTableCell-head': {
            bgcolor: BRAND_COLORS.oceanTeal,
            color: BRAND_COLORS.compassWhite,
            fontWeight: 500,
            whiteSpace: 'nowrap',
          },
          '& td.metric-band': { bgcolor: BRAND_COLORS.sandyBeige },
          '& td.group-start': { borderTop: 1, borderColor: 'divider' },
          '& tr': { breakInside: 'avoid' },
        }}
      >
        <TableHead>
          <TableRow>
            {headings.map((label) => (
              <TableCell key={label}>{label}</TableCell>
            ))}
            {values.map(([label]) => (
              <TableCell key={label} align="right">
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((row, index) => {
            const band = positions[index] % 2 ? 'metric-band' : undefined;
            const changed = firstChange(index);
            const opens = (depth: number) => changed > 0 && changed <= depth;
            const cell = (depth: number, className?: string) =>
              [className, opens(depth) ? 'group-start' : undefined].filter(Boolean).join(' ') || undefined;
            return (
              <TableRow key={row.id}>
                {[1, 2, 3].map((depth) => (
                  <TableCell key={depth} className={cell(depth)}>
                    {opens(depth) ? ([row.rsmRegion, row.person, row.region][depth - 1] ?? 'Unknown') : null}
                  </TableCell>
                ))}
                <TableCell className={cell(3, band)}>{row.label}</TableCell>
                <TableCell className={cell(3, band)}>{row.onTrack}</TableCell>
                {values.map(([label, value, formatter]) => (
                  <TableCell key={label} className={cell(3, band)} align="right" sx={{ color: statusColour(row) }}>
                    {fixed(value(row), formatter)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {!shown.length && (
            <TableRow>
              <TableCell colSpan={headings.length + values.length}>No rows for this quarter.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
